import React, { useEffect, useMemo, useCallback, useRef, useState } from 'react'
import RightPanel from 'components/RightPanel/RightPanel'
import AssigneePopover from 'components/AssigneePopover/AssigneePopover'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import {
	initializeAssignedTaskCNs,
	updateTaskCN,
	reorderTaskCNs,
	setSearchQuery,
	setFilterPriority,
	setFilterProject,
	setFilterAssignee,
	setShowCompleted,
	setPaginationEnabled,
	setSelectedTask,
} from 'slices/taskCNSlice'
import axios from 'axios'
import Layout from 'components/Layout'
import { Spinner } from 'components/ui/spinner'
import AdminPanelCN from 'components/AdminPanelCN/AdminPanelCN'
import HeaderCN from 'components/HeaderCN/HeaderCN'
import { Button } from 'components/ui/button'
import { Select } from 'components/ui/select'
import { Switch } from 'components/ui/switch'
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from 'components/ui/table'
import { TablePagination } from 'components/ui/pagination'
import useIsMobile from 'hooks/useIsMobile'
import { toast } from 'components/ui/toast-store'
import { GitBranch, Plus, Filter, Check, Eye, Calendar, List } from 'lucide-react'
import { TableSortIcon } from 'components/TableSortIcon'
import { Avatar, AvatarImage, AvatarFallback } from 'components/ui/avatar'
import TasksCalendar from 'components/TasksCalendar/TasksCalendar'
import TasksTimeline from 'components/TasksTimeline/TasksTimeline'
import SortableTasksTableBody from 'components/TasksList/SortableTasksTableBody'
import CreateTaskCNModal from './CreateTaskCNModal'
import TaskCNDetailSheet from './TaskCNDetailSheet'
import SidebarTasksCNRecent from './SidebarTasksCNRecent'
import { cn } from 'lib/utils'
import { formatScheduledTime, filterTasks, sortTaskList, collectAssignees, isOverdue } from 'utils/tasks'
import { canCompleteTask } from 'utils/taskPermissions'
import { ArrowRight } from 'lucide-react'
import routes from 'constants/routes'
import MobileBackButton from 'components/MobileBackButton'

// Format project display label: "Project Name - Asset Name"
function formatProjectLabel(project: any): string {
	if (!project) return ''
	const assetName = project.projectAsset?.assetName
	return assetName ? `${project.projectName} - ${assetName}` : project.projectName
}

// Priority badge - bordered with rounded corners (matches orbitea style)
const PRIORITY_STYLES: Record<string, { text: string; bg: string; border: string }> = {
	'P1 - Urgent': { text: 'rgb(239, 68, 68)', bg: 'rgba(239, 68, 68, 0.1)', border: 'rgba(239, 68, 68, 0.2)' },
	'P2 - High': { text: 'rgb(249, 115, 22)', bg: 'rgba(249, 115, 22, 0.1)', border: 'rgba(249, 115, 22, 0.2)' },
	'P3 - Medium': { text: 'rgb(62, 124, 177)', bg: 'rgba(62, 124, 177, 0.1)', border: 'rgba(62, 124, 177, 0.2)' },
	'P4 - Low': { text: 'rgb(113, 113, 122)', bg: 'rgba(113, 113, 122, 0.1)', border: 'rgba(113, 113, 122, 0.2)' },
}

function PriorityBadge({ priority }: { priority: string }) {
	if (!priority) return <span className="text-gray-400">-</span>

	const label = priority.split(' - ')[0] || priority
	const style = PRIORITY_STYLES[priority]
	if (!style) return <span className="text-gray-400">{label}</span>

	return (
		<span
			className="inline-flex items-center justify-center px-2 py-0.5 rounded-md text-xs font-semibold border"
			style={{ color: style.text, backgroundColor: style.bg, borderColor: style.border }}
		>
			{label}
		</span>
	)
}

// Generate a stable color from a string (for project color variability)
const PROJECT_COLORS = [
	'#3b82f6',
	'#8b5cf6',
	'#ec4899',
	'#f59e0b',
	'#10b981',
	'#06b6d4',
	'#6366f1',
	'#f43f5e',
	'#84cc16',
	'#14b8a6',
]

function getProjectColor(id: string): string {
	let hash = 0
	for (let i = 0; i < id.length; i++) {
		hash = id.charCodeAt(i) + ((hash << 5) - hash)
	}
	return PROJECT_COLORS[Math.abs(hash) % PROJECT_COLORS.length]
}

// Project pill with folder icon and dynamic color — clicks navigate to project tasks
function ProjectPill({ project, onClick }: { project: any; onClick?: (e: React.MouseEvent) => void }) {
	if (!project?.projectName) return null

	const color = getProjectColor(project._id || '')
	return (
		<button
			type="button"
			onClick={onClick}
			className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-xs font-medium bg-transparent cursor-pointer hover:opacity-80 transition-opacity"
			style={{
				color,
				backgroundColor: `${color}1a`,
				borderColor: `${color}33`,
			}}
		>
			<GitBranch className="w-3 h-3" />
			{formatProjectLabel(project)}
		</button>
	)
}

// Effort badge
function EffortBadge({ effort }: { effort: string }) {
	if (!effort) return <span className="text-gray-400">-</span>
	const label = effort.split(' - ')[0] || effort
	return <span className="text-sm text-gray-500">{label}</span>
}

// Format due date
function formatDueDate(dateStr: string | null): string {
	if (!dateStr) return '-'
	const d = new Date(dateStr)
	const taskDate = new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())
	const today = new Date()
	today.setHours(0, 0, 0, 0)
	const tomorrow = new Date(today)
	tomorrow.setDate(tomorrow.getDate() + 1)

	if (taskDate.getTime() === today.getTime()) return 'Today'
	if (taskDate.getTime() === tomorrow.getTime()) return 'Tomorrow'
	return taskDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

// Task checkbox
function TaskCheckbox({
	checked,
	onChange,
	disabled = false,
	title,
}: {
	checked: boolean
	onChange: () => void
	disabled?: boolean
	title?: string
}) {
	return (
		<button
			type="button"
			disabled={disabled}
			title={title}
			onClick={e => {
				e.stopPropagation()
				onChange()
			}}
			className={`w-4 h-4 min-w-[1rem] max-w-[1rem] min-h-[1rem] max-h-[1rem] flex-shrink-0 rounded-full border-2 flex items-center justify-center transition-all ${
				checked ? 'bg-gray-700 border-gray-700 text-white' : 'bg-transparent border-gray-400 hover:border-gray-600'
			} ${disabled ? 'opacity-40 cursor-not-allowed hover:border-gray-400' : ''}`}
		>
			{checked && <Check className="w-2 h-2" />}
		</button>
	)
}

// Empty state
function EmptyState({ onCreateClick }: { onCreateClick: () => void }) {
	return (
		<div className="flex flex-col items-center justify-center py-16">
			<div className="w-16 h-16 rounded-full flex items-center justify-center mb-4 bg-gray-100">
				<svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth={1.5}
						d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
					/>
				</svg>
			</div>
			<h2 className="text-lg font-semibold text-gray-900 mb-1">No tasks found</h2>
			<p className="text-sm text-gray-500 mb-4">Create your first task to get started</p>
			<Button onClick={onCreateClick}>
				<Plus className="w-4 h-4 mr-1" />
				New Task
			</Button>
		</div>
	)
}

// Sort icon now lives in components/TableSortIcon so the Home tasks card can
// share it. Aliased to keep the call sites below unchanged.
const SortIcon = TableSortIcon

// Header cell definitions. The leading `dragHandle` slot reserves space for the
// grip column rendered by SortableTasksTableBody.
const headerCells = [
	{ id: 'dragHandle', label: '', sortable: false, hideOnMobile: false, hideOnDesktop: false },
	{ id: 'assignedTo-mobile', label: '', sortable: false, hideOnMobile: false, hideOnDesktop: true },
	{ id: 'completed', label: '', sortable: false, hideOnMobile: true, hideOnDesktop: false },
	{ id: 'taskName', label: 'Task', sortable: true, hideOnMobile: false, hideOnDesktop: false },
	{ id: 'assignedTo', label: 'Assigned', sortable: true, hideOnMobile: true, hideOnDesktop: false },
	{ id: 'taskPriority', label: 'Priority', sortable: true, hideOnMobile: true, hideOnDesktop: false },
	{ id: 'taskEffort', label: 'Effort', sortable: true, hideOnMobile: true, hideOnDesktop: false },
	{ id: 'dateDue', label: 'Due Date', sortable: true, hideOnMobile: false, hideOnDesktop: false },
	// Its own column (MYD-534): filled only when the task has a scheduled time.
	{ id: 'time', label: 'Time', sortable: false, hideOnMobile: true, hideOnDesktop: false },
	{ id: 'actions', label: '', sortable: false, hideOnMobile: false, hideOnDesktop: false },
]

const TasksCN_Index = () => {
	const dispatch = useDispatch()
	const navigate = useNavigate()
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
	const [viewMode, setViewMode] = useState<'table' | 'calendar' | 'timeline'>('table')
	const [showCreateModal, setShowCreateModal] = useState(false)
	const [userPopup, setUserPopup] = useState<{ user: any; anchor: DOMRect } | null>(null)
	const searchInputRef = useRef<HTMLInputElement>(null)

	// Sorting state
	const [orderBy, setOrderBy] = useState<string | null>('dateDue')
	const [order, setOrder] = useState<'asc' | 'desc'>('desc')

	// Pagination state
	const [page, setPage] = useState(0)
	const [rowsPerPage, setRowsPerPage] = useState(10)

	const { userInfo } = useSelector((state: any) => state.userLogin)
	const {
		entities,
		ids,
		isLoading,
		isInitialized,
		selectedTask,
		searchQuery,
		filterPriority,
		filterProject,
		filterAssignee,
		showCompleted,
		paginationEnabled,
	} = useSelector((state: any) => state.taskCNs)

	// Fetch projects directly (bypasses shared project slice which can be overwritten by other screens)
	const [activeProjects, setActiveProjects] = useState<any[]>([])
	useEffect(() => {
		if (userInfo?.token) {
			axios
				.get('/api/projects/myProjects', { headers: { Authorization: `Bearer ${userInfo.token}` } })
				.then(res => {
					const projects = (res.data || []).filter((p: any) => p.isActive !== false)
					setActiveProjects(projects)
				})
				.catch(() => {})
		}
	}, [userInfo])

	// Initialize tasks
	useEffect(() => {
		if (userInfo?._id) {
			dispatch(initializeAssignedTaskCNs({ assignedTo: userInfo._id }) as any)
		}
	}, [dispatch, userInfo, isInitialized])

	// Get all tasks from entities
	const allTasks = useMemo(() => ids.map((id: string) => entities[id]).filter(Boolean), [ids, entities])

	// Assignee filter options, derived from the tasks in hand so the dropdown only
	// offers people who actually appear in this list.
	const assigneeOptions = useMemo(() => collectAssignees(allTasks), [allTasks])

	const filteredTasks = useMemo(
		() => filterTasks(allTasks, { searchQuery, filterPriority, filterProject, filterAssignee, showCompleted }),
		[allTasks, searchQuery, filterPriority, filterProject, filterAssignee, showCompleted]
	)

	const sortedTasks = useMemo(() => sortTaskList(filteredTasks, orderBy, order), [filteredTasks, orderBy, order])

	// Mobile lists are unpaginated and scrolled.
	// `paginationEnabled` lives in redux and is a user preference, so it is
	// COERCED for rendering here and never written to. Dispatching it false from
	// a phone would silently change the same user's desktop setting.
	const isMobile = useIsMobile()
	const showPagination = paginationEnabled && !isMobile

	// Paginate tasks — short-circuit when pagination is disabled
	const paginatedTasks = useMemo(() => {
		if (!showPagination) return sortedTasks
		const start = page * rowsPerPage
		return sortedTasks.slice(start, start + rowsPerPage)
	}, [sortedTasks, page, rowsPerPage, showPagination])

	// Reset page on filter/sort changes (and when pagination is toggled on/off)
	useEffect(() => {
		setPage(0)
	}, [searchQuery, filterPriority, filterProject, filterAssignee, showCompleted, orderBy, order, paginationEnabled])

	const handleSort = (columnId: string) => {
		if (orderBy === columnId) {
			setOrder(prev => (prev === 'asc' ? 'desc' : 'asc'))
		} else {
			setOrderBy(columnId)
			setOrder('asc')
		}
	}

	const handleToggleComplete = useCallback(
		async (task: any) => {
			if (!canCompleteTask(task, userInfo?._id)) return
			if (task.completed) {
				dispatch(updateTaskCN({ _id: task._id, completed: false, dateCompleted: null }) as any)
				return
			}
			const confirmed = await toast.confirm({
				title: 'Complete Task',
				message: `Mark "${task.taskName}" as completed?`,
				confirmLabel: 'Set as Completed',
				cancelLabel: 'Cancel',
				variant: 'default',
			})
			if (!confirmed) return
			const now = new Date().toISOString()
			dispatch(updateTaskCN({ _id: task._id, completed: true, dateCompleted: now }) as any)
		},
		[dispatch, userInfo]
	)

	const handleSelectTask = useCallback(
		(task: any) => {
			dispatch(setSelectedTask(task))
		},
		[dispatch]
	)

	const handleCloseDetail = useCallback(() => {
		dispatch(setSelectedTask(null))
	}, [dispatch])

	const handleAvatarClick = useCallback((e: React.MouseEvent, user: any) => {
		e.stopPropagation()
		const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
		setUserPopup({ user, anchor: rect })
	}, [])

	const handleProjectPillClick = useCallback(
		(e: React.MouseEvent, project: any) => {
			e.stopPropagation()
			if (project?._id) {
				navigate(`${routes.PROJECTS}/${project._id}/tasks`)
			}
		},
		[navigate]
	)

	// Transform tasks for Calendar/Timeline views
	const tasksForCalendarTimeline = useMemo(
		() =>
			allTasks.map((t: any) => ({
				...t,
				completedBy: t.dateDue,
				taskStatus: t.completed ? 'Completed' : 'Open',
			})),
		[allTasks]
	)

	const handleCalendarTimelineTaskClick = useCallback(
		(task: any) => {
			const taskCN = entities[task._id]
			if (taskCN) {
				dispatch(setSelectedTask(taskCN))
			}
		},
		[dispatch, entities]
	)

	const handleCalendarDateChange = useCallback(
		(task: any, newDate: string) => {
			dispatch(updateTaskCN({ _id: task._id, dateDue: newDate }) as any)
		},
		[dispatch]
	)

	return (
		<div className="flex h-screen overflow-hidden bg-gray-50">
			<AdminPanelCN mobileOpen={mobileMenuOpen} onMobileClose={() => setMobileMenuOpen(false)} />

			<div className="flex-1 flex flex-col overflow-hidden">
				<HeaderCN onMobileMenuToggle={() => setMobileMenuOpen(!mobileMenuOpen)} />

				<div className="flex-1 flex overflow-hidden">
					<main className="flex-1 overflow-y-auto p-3 md:p-6">
						{/* The close button self-gates on viewport, so this row is the same
						    shape on desktop with nothing rendered on the right. */}
						<div className="flex items-center justify-between gap-3 mb-4">
							<h2 className="text-xl md:text-2xl font-semibold text-gray-800">Tasks</h2>
							<MobileBackButton mode="close" />
						</div>

						{/* Toolbar */}
						<Layout.PageToolbarFilter
							buttonText="New Task"
							buttonOnClick={() => setShowCreateModal(true)}
							customButtons={
								<div className="inline-flex items-center rounded-lg bg-gray-100 p-0.5">
									<button
										type="button"
										onClick={() => setViewMode('table')}
										className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-all ${
											viewMode === 'table' ? 'bg-white text-[#0080fe] shadow-sm' : 'text-gray-500 hover:text-[#0080fe]'
										}`}
										title="List View"
									>
										<List className="w-3.5 h-3.5" />
										List
									</button>
									<button
										type="button"
										onClick={() => setViewMode('calendar')}
										className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-all ${
											viewMode === 'calendar' ? 'bg-white text-[#0080fe] shadow-sm' : 'text-gray-500 hover:text-[#0080fe]'
										}`}
										title="Calendar View"
									>
										<Calendar className="w-3.5 h-3.5" />
										Calendar
									</button>
									<button
										type="button"
										onClick={() => setViewMode('timeline')}
										className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-all ${
											viewMode === 'timeline' ? 'bg-white text-[#0080fe] shadow-sm' : 'text-gray-500 hover:text-[#0080fe]'
										}`}
										title="Timeline View"
									>
										<GitBranch className="w-3.5 h-3.5" />
										Timeline
									</button>
								</div>
							}
							searchInputLabel=""
							searchInputRef={searchInputRef}
							searchInputValue={searchQuery}
							searchInputOnChange={(e: any) => dispatch(setSearchQuery(e.target.value))}
						/>

						{viewMode === 'table' ? (
							<>
								{/* Task-specific filters */}
								<div className="flex items-center gap-2 md:gap-4 flex-wrap mb-4 mt-4">
									<div className="flex items-center gap-2 flex-wrap">
										<Filter className="w-3.5 h-3.5 text-gray-400" />
										<Select
											value={filterPriority}
											onChange={e => dispatch(setFilterPriority(e.target.value))}
											className="w-[130px] md:w-[150px]"
										>
											<option value="">All Priorities</option>
											<option value="P1 - Urgent">P1 - Urgent</option>
											<option value="P2 - High">P2 - High</option>
											<option value="P3 - Medium">P3 - Medium</option>
											<option value="P4 - Low">P4 - Low</option>
										</Select>
										<Select
											value={filterProject}
											onChange={e => dispatch(setFilterProject(e.target.value))}
											className="w-[150px] md:w-[180px]"
										>
											<option value="">All Projects</option>
											{activeProjects.map((p: any) => (
												<option key={p._id} value={p._id}>
													{formatProjectLabel(p)}
												</option>
											))}
										</Select>
										<Select
											value={filterAssignee}
											onChange={e => dispatch(setFilterAssignee(e.target.value))}
											className="w-[130px] md:w-[160px]"
										>
											<option value="">All Assignees</option>
											{assigneeOptions.map(a => (
												<option key={a.id} value={a.id}>
													{a.label}
												</option>
											))}
										</Select>
									</div>
									<div className="flex items-center gap-4 ml-auto">
										{/* Hidden on mobile, where lists are always unpaginated and this
										    switch would be a control that does nothing. */}
										{!isMobile && (
										<label className="flex items-center gap-2 text-sm text-gray-500 cursor-pointer">
											<Switch
												checked={paginationEnabled}
												onChange={e => dispatch(setPaginationEnabled(e.target.checked))}
											/>
											<span>Enable pagination</span>
										</label>
										)}
										<label className="flex items-center gap-2 text-sm text-gray-500 cursor-pointer">
											<Switch checked={showCompleted} onChange={e => dispatch(setShowCompleted(e.target.checked))} />
											<span>Show completed</span>
										</label>
									</div>
								</div>

								{/* Table Content */}
								{isLoading ? (
									<Spinner containerHeight="50vh" />
								) : sortedTasks.length > 0 ? (
									<div className="rounded-lg border border-gray-200 overflow-hidden shadow-sm bg-white overflow-x-auto">
										<Table>
											<TableHeader>
												<TableRow className="bg-gray-50 hover:bg-gray-50">
													{headerCells.map(cell => (
														<TableHead
															key={cell.id}
															className={cn(
																cell.sortable
																	? 'cursor-pointer select-none text-gray-700 text-xs font-semibold whitespace-nowrap'
																	: 'text-gray-700 text-xs font-semibold whitespace-nowrap',
																cell.id === 'completed' && 'w-10',
																cell.id === 'assignedTo-mobile' && 'w-10 text-center',
																cell.id === 'taskName' && 'min-w-[120px] md:min-w-[200px]',
																cell.id === 'assignedTo' && 'w-10 text-center',
																cell.id === 'taskPriority' && 'w-24 text-center',
																cell.id === 'taskEffort' && 'w-20 text-center',
																cell.id === 'dateDue' && 'w-28 text-center',
																cell.id === 'time' && 'w-24 text-center',
																cell.id === 'actions' && 'w-10',
																cell.hideOnMobile && 'hidden md:table-cell',
																cell.hideOnDesktop && 'md:hidden'
															)}
															onClick={cell.sortable ? () => handleSort(cell.id) : undefined}
														>
															<span className="inline-flex items-center">
																{cell.label}
																{cell.sortable && <SortIcon columnId={cell.id} orderBy={orderBy} order={order} />}
															</span>
														</TableHead>
													))}
												</TableRow>
											</TableHeader>
											<SortableTasksTableBody
												tasks={paginatedTasks}
												columnCount={headerCells.length}
												orderBy={orderBy}
												rowClassName={(task: any) =>
													cn(
														'cursor-pointer group hover:bg-[#fffbf2]',
														task.completed && 'opacity-60',
														selectedTask?._id === task._id && 'bg-blue-50',
													)
												}
												onRowClick={(task: any) => handleSelectTask(task)}
												onReorder={ids => dispatch(reorderTaskCNs(ids) as any)}
												renderRowCells={(task: any) => {
													const overdue = isOverdue(task.dateDue) && !task.completed
													const predecessor =
														typeof task.predecessorTaskId === 'object' ? task.predecessorTaskId : null
													return (
														<>
															{/* Assigned — mobile only */}
															<TableCell className="text-center md:hidden">
																<button
																	type="button"
																	className="bg-transparent border-none p-0 m-0 cursor-pointer mx-auto block"
																	onClick={e => handleAvatarClick(e, task.assignedTo)}
																	title={task.assignedTo?.name || task.assignedTo?.firstName || ''}
																>
																	<Avatar className="h-7 w-7">
																		<AvatarImage src={task.assignedTo?.image} alt={task.assignedTo?.name || ''} />
																		<AvatarFallback className="text-xs bg-gray-200 text-gray-600">
																			{(task.assignedTo?.name || task.assignedTo?.firstName || '?')
																				.charAt(0)
																				.toUpperCase()}
																		</AvatarFallback>
																	</Avatar>
																</button>
															</TableCell>
															{/* Completed — desktop only */}
															<TableCell className="hidden md:table-cell">
																<TaskCheckbox
																	checked={task.completed}
																	onChange={() => handleToggleComplete(task)}
																	disabled={!canCompleteTask(task, userInfo?._id)}
																	title={
																		canCompleteTask(task, userInfo?._id)
																			? undefined
																			: 'Only the task creator, the assignee or the owner can complete this task'
																	}
																/>
															</TableCell>
															<TableCell>
																<span
																	className={`text-sm font-medium ${
																		task.completed ? 'line-through text-gray-400' : 'text-gray-900'
																	}`}
																>
																	{task.taskName}
																</span>
																<ProjectPill
																	project={task.projectid}
																	onClick={e => handleProjectPillClick(e, task.projectid)}
																/>
																{predecessor?.taskName && (
																	<span className="ml-2 inline-flex items-center gap-1 text-[11px] text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
																		<ArrowRight className="w-3 h-3" />
																		<span title={predecessor.taskName}>follows {predecessor.taskName}</span>
																	</span>
																)}
																{task.completed && task.dateCompleted && (
																	<span className="ml-2 text-xs text-gray-400">
																		Date Completed: {new Date(task.dateCompleted).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}
																	</span>
																)}
															</TableCell>
															{/* Assigned — desktop only */}
															<TableCell className="text-center hidden md:table-cell">
																<button
																	type="button"
																	className="bg-transparent border-none p-0 m-0 cursor-pointer mx-auto block"
																	onClick={e => handleAvatarClick(e, task.assignedTo)}
																	title={task.assignedTo?.name || task.assignedTo?.firstName || ''}
																>
																	<Avatar className="h-7 w-7">
																		<AvatarImage src={task.assignedTo?.image} alt={task.assignedTo?.name || ''} />
																		<AvatarFallback className="text-xs bg-gray-200 text-gray-600">
																			{(task.assignedTo?.name || task.assignedTo?.firstName || '?')
																				.charAt(0)
																				.toUpperCase()}
																		</AvatarFallback>
																	</Avatar>
																</button>
															</TableCell>
															<TableCell className="text-center hidden md:table-cell">
																<PriorityBadge priority={task.taskPriority} />
															</TableCell>
															<TableCell className="text-center hidden md:table-cell">
																<EffortBadge effort={task.taskEffort} />
															</TableCell>
															<TableCell className="text-center">
																<span
																	className={`text-sm inline-flex items-center gap-1 ${
																		overdue ? 'text-red-500' : 'text-gray-500'
																	}`}
																>
																	{task.dateDue && <Calendar className="w-3 h-3" />}
																	{formatDueDate(task.dateDue)}
																</span>
															</TableCell>
															<TableCell className="text-center hidden md:table-cell text-sm text-gray-500 whitespace-nowrap">
																{task.hasScheduledTime ? formatScheduledTime(task.dateDue) : ''}
															</TableCell>
															<TableCell>
																<button
																	type="button"
																	className="p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-[#0080fe]"
																	onClick={e => {
																		e.stopPropagation()
																		handleSelectTask(task)
																	}}
																	title="View details"
																>
																	<Eye className="w-4 h-4" />
																</button>
															</TableCell>
														</>
													)
												}}
											/>
										</Table>
										{showPagination && sortedTasks.length > 10 && (
											<TablePagination
												page={page}
												rowsPerPage={rowsPerPage}
												count={sortedTasks.length}
												onPageChange={setPage}
												onRowsPerPageChange={setRowsPerPage}
											/>
										)}
									</div>
								) : (
									<EmptyState onCreateClick={() => setShowCreateModal(true)} />
								)}
							</>
						) : viewMode === 'timeline' ? (
							<div className="mt-4" style={{ width: '100%', maxWidth: '100%' }}>
								<TasksTimeline
									tasks={tasksForCalendarTimeline}
									onTaskClick={handleCalendarTimelineTaskClick}
									onTaskDateChange={handleCalendarDateChange}
								/>
							</div>
						) : (
							<div className="mt-4" style={{ width: '100%', maxWidth: '100%' }}>
								<TasksCalendar tasks={tasksForCalendarTimeline} onTaskClick={handleCalendarTimelineTaskClick} onTaskDateChange={handleCalendarDateChange} />
							</div>
						)}
					</main>

					<RightPanel storageKey="tasks" label="Tasks panel" className="overflow-y-auto">
						<SidebarTasksCNRecent />
					</RightPanel>
				</div>
			</div>

			{/* Create Task Modal */}
			<CreateTaskCNModal open={showCreateModal} onClose={() => setShowCreateModal(false)} />

			{/* Task Detail Sheet */}
			<TaskCNDetailSheet task={selectedTask} open={!!selectedTask} onClose={handleCloseDetail} />

			{/* Assignee popup: email link, and tap-to-call on mobile (MYD-534) */}
			{userPopup && (
				<AssigneePopover user={userPopup.user} anchor={userPopup.anchor} onClose={() => setUserPopup(null)} />
			)}
		</div>
	)
}

export default TasksCN_Index
